"use client";

import type { CreateProfileFormData } from "@/features/profile/tutor/schemas/createProfileSchema.ts";
import type { LangResponse, Language } from "@/features/profile/tutor/types/languages";
import type React from "react";

import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from "react";
import { type Control, useFieldArray, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

import { Input } from "@/components/ui/input";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { Card, CardContent } from "@/components/ui/card";

interface SelectLanguagesStepProps {
  control: Control<CreateProfileFormData>;
  setNavButtonsDisabled: Dispatch<SetStateAction<boolean>>;
}

// Define selection steps for consistent navigation
enum SelectionStep {
  LANGUAGE_LIST = 0,
  PROFICIENCY_SELECTION = 1,
}

const PROFICIENCY_LEVELS = [
  { id: "basic", label: "Beginner", icon: "icon-park-solid:pie-two" },
  { id: "conversational", label: "Intermediate", icon: "icon-park-solid:pie-four" },
  { id: "fluent", label: "Fluent", icon: "icon-park-solid:pie-six" },
  { id: "native", label: "Native", icon: "material-symbols:circle" },
];

export function SelectLanguages({ control, setNavButtonsDisabled }: SelectLanguagesStepProps) {
  const { t } = useTranslation(["profile", "common"]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [search, setSearch] = useState("");
  const [currentStep, setCurrentStep] = useState<SelectionStep>(SelectionStep.LANGUAGE_LIST);
  const [languagesLoaded, setLanguagesLoaded] = useState(false);

  const languagesForm = useWatch({ control, name: "languages" });
  const languageCode = useWatch({ control, name: "locale_code" });
  const { append, remove } = useFieldArray({
    control,
    name: "languages",
  });

  // Load languages on component mount
  useEffect(() => {
    getLanguages();
  }, []);

  useEffect(() => {
    if (currentStep === SelectionStep.LANGUAGE_LIST) {
      setNavButtonsDisabled(false);
    } else {
      setNavButtonsDisabled(true);
    }
  }, [currentStep]);

  async function getLanguages() {
    try {
      setIsLoading(true);
      const { data } = await axiosApiInstance.get<LangResponse>(
        API_ENDPOINTS.TUTOR.UTILS.LANGUAGES,
        {
          params: { locale_code: languageCode, search: "" },
        },
      );

      setLanguages(data.data);
      setLanguagesLoaded(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleLanguageSelect = (language: Language) => {
    // Check if language is already selected
    const isSelected = languagesForm?.some((lang) => lang.id === language.id);

    if (isSelected) {
      // If selected, remove it
      const index = languagesForm.findIndex((lang) => lang.id === language.id);

      if (index !== -1) {
        remove(index);
      }
    } else {
      // If not selected, proceed to proficiency selection
      setSelectedLanguage(language);
      setCurrentStep(SelectionStep.PROFICIENCY_SELECTION);
    }
  };

  const handleProficiencySelect = (proficiency: string) => {
    if (selectedLanguage) {
      // Add the language first
      append({
        id: selectedLanguage.id,
        proficiency,
      });

      // Clear the selected language
      setSelectedLanguage(null);

      // Always go back to language list after selecting proficiency
      setCurrentStep(SelectionStep.LANGUAGE_LIST);
      setSearch("");
    }
  };

  const navigateBack = () => {
    if (currentStep === SelectionStep.PROFICIENCY_SELECTION) {
      setSelectedLanguage(null);
      setCurrentStep(SelectionStep.LANGUAGE_LIST);
    }
    setSearch("");
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  // Filter languages based on search term
  const filteredLanguages = useMemo(() => {
    if (search.length >= 2) {
      return languages.filter((lang) => lang.name.toLowerCase().includes(search.toLowerCase()));
    }

    return languages;
  }, [languages, search]);

  // Check if a language is selected
  const isLanguageSelected = (languageId: string) => {
    return languagesForm?.some((lang) => lang.id === languageId) || false;
  };

  // Get the proficiency of a selected language
  const getSelectedLanguageProficiency = (languageId: string) => {
    const selected = languagesForm?.find((lang) => lang.id === languageId);

    return selected?.proficiency;
  };

  // Simplified animation transitions
  const pageTransition = {
    duration: 0.3,
    ease: "easeInOut",
  };

  // Render language list view
  const renderLanguageListStep = () => {
    // Show loading state if languages aren't loaded yet
    if (!languagesLoaded) {
      return (
        <motion.div
          key="language-list-loading"
          className="absolute inset-0 flex w-full items-center justify-center"
          exit={{ opacity: 0 }}
          initial={{ opacity: 1 }}
        >
          <div className="flex flex-col items-center gap-2">
            <div className="border-accent-foreground h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
            <span className="text-muted-foreground">
              {t("profile:selectLanguages.loadingLanguages")}
            </span>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        key="language-list"
        animate={{ opacity: 1 }}
        className="absolute inset-0 w-full"
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        transition={pageTransition}
      >
        <div className="mb-6 text-center">
          <div className="bg-accent-foreground/5 mb-3 inline-flex h-16 w-16 items-center justify-center rounded-full">
            <Icon className="text-accent-foreground" height={32} icon="tabler:world" width={32} />
          </div>
          <h1 className="mb-2 text-xl font-bold sm:text-2xl">
            {t("profile:selectLanguages.languages")}
          </h1>
          <p className="text-muted-foreground mx-auto max-w-md text-sm sm:text-base">
            {t("profile:selectLanguages.subtitle")}
          </p>
        </div>

        <div className="mx-auto mb-4 max-w-2xl">
          <div className="relative flex items-center">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Icon className="text-muted-foreground" height={20} icon="tabler:search" width={20} />
            </div>
            <Input
              className="bg-side-accent/50 h-12 rounded-xl pl-10 text-base transition-all focus-visible:ring-1 focus-visible:ring-offset-1"
              placeholder={t("profile:selectLanguages.searchLanguages")}
              type="text"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        <div className="mx-auto max-w-2xl">
          <ScrollArea className="-mx-4 h-[55dvh] px-4 pb-6">
            {isLoading ? (
              <div className="flex h-full items-center justify-center p-4">
                <div className="flex flex-col items-center gap-2">
                  <div className="border-accent-foreground h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
                  <span className="text-muted-foreground">
                    {t("profile:selectLanguages.loadingLanguages")}
                  </span>
                </div>
              </div>
            ) : filteredLanguages.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-2">
                {filteredLanguages.map((language, index) => {
                  const isSelected = isLanguageSelected(language.id);
                  const proficiency = getSelectedLanguageProficiency(language.id);

                  return (
                    <motion.div
                      key={language.id}
                      animate={{ opacity: 1, y: 0 }}
                      className="cursor-pointer p-1"
                      initial={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2, delay: Math.min(0.01 * index, 0.3) }}
                    >
                      <Card
                        className={`border-none transition-all duration-200 ${
                          isSelected
                            ? "bg-accent-foreground border-accent-foreground"
                            : "hover:bg-accent-foreground/5 bg-muted/20"
                        }`}
                        onClick={() => handleLanguageSelect(language)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex min-h-[3rem] flex-grow flex-col items-center justify-center text-center">
                              <span
                                className={`text-sm font-medium sm:text-base ${
                                  isSelected ? "text-primary" : "text-muted-foreground"
                                }`}
                              >
                                {language.name}
                              </span>
                              {isSelected && proficiency && (
                                <p className="text-primary/80 text-[0.7rem] sm:text-xs">
                                  {t(
                                    //@ts-ignore
                                    `profile:selectLanguages.proficiencyLevels.${proficiency}`,
                                  )}
                                </p>
                              )}
                            </div>
                            {isSelected ? (
                              <Icon
                                className="text-primary ml-2"
                                height={20}
                                icon="tabler:check"
                                width={20}
                              />
                            ) : (
                              <Icon
                                className="text-muted-foreground ml-2"
                                height={20}
                                icon="tabler:chevron-right"
                                width={20}
                              />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center p-4">
                <div className="text-center">
                  <Icon
                    className="text-muted-foreground mx-auto mb-3"
                    height={48}
                    icon={search.length >= 2 ? "tabler:search-off" : "tabler:world"}
                    width={48}
                  />
                  <span className="text-muted-foreground block">
                    {search.length >= 2
                      ? t("profile:selectLanguages.noMatchingLanguages")
                      : t("profile:selectLanguages.noLanguagesAvailable")}
                  </span>
                </div>
              </div>
            )}
          </ScrollArea>
        </div>
      </motion.div>
    );
  };

  // Render proficiency selection step
  const renderProficiencySelectionStep = () => {
    if (!selectedLanguage) return null;

    return (
      <motion.div
        key="proficiency-selection"
        animate={{ opacity: 1 }}
        className="absolute inset-0 w-full"
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        transition={pageTransition}
      >
        <div className="mb-6">
          <div className="mb-4 w-full text-left">
            <Button className="h-9 rounded-full" size="sm" variant="outline" onClick={navigateBack}>
              <Icon className="mr-1" height={16} icon="tabler:chevron-left" width={16} />
              {t("profile:selectLanguages.selectLanguage")}
            </Button>
          </div>

          <div className="text-center">
            <div className="bg-accent-foreground/5 mb-3 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <Icon className="text-accent-foreground" height={32} icon="tabler:world" width={32} />
            </div>
            <h1 className="mb-2 text-2xl font-bold">{selectedLanguage.name}</h1>
            <p className="text-muted-foreground mx-auto max-w-md">
              {t("profile:selectLanguages.selectProficiency")}
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-xl">
          <div className="grid grid-cols-2 gap-4 p-2">
            {PROFICIENCY_LEVELS.map((level) => (
              <motion.div
                key={level.id}
                animate={{ opacity: 1, y: 0 }}
                className="cursor-pointer p-1"
                initial={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.3, delay: 0.05 * PROFICIENCY_LEVELS.indexOf(level) }}
              >
                <Card
                  className="hover:bg-accent-foreground/5 bg-muted/20 h-full border-none transition-all duration-200"
                  onClick={() => handleProficiencySelect(level.id)}
                >
                  <CardContent className="flex flex-col items-center p-4 text-center">
                    <div className="bg-accent-foreground mb-3 flex h-12 w-12 items-center justify-center rounded-full">
                      <Icon className="text-primary" height={24} icon={level.icon} width={24} />
                    </div>
                    <div className="text-muted-foreground text-base font-medium">
                      {t(
                        //@ts-ignore
                        `profile:selectLanguages.proficiencyLevels.${level.id}`,
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="mx-auto max-h-[calc(100dvh_-_10rem)] w-full max-w-3xl px-4 py-6 md:px-6">
      <FormField
        control={control}
        name="languages"
        render={() => (
          <FormItem className="space-y-3">
            <FormControl>
              <div className="relative h-[65dvh] w-full overflow-hidden md:h-[70dvh]">
                <AnimatePresence initial={false} mode="wait">
                  {currentStep === SelectionStep.LANGUAGE_LIST && renderLanguageListStep()}
                  {currentStep === SelectionStep.PROFICIENCY_SELECTION &&
                    renderProficiencySelectionStep()}
                </AnimatePresence>
              </div>
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}
